<template>
  <section class="AiReviewsBlock">
    <div class="Layout">
      <div class="Layout-section">
        <grid-item class="AiReviews-header" sm="4" md="4" offset-md="1">
          <ai-typo class="AiReviews-title" as="h2" variant="heading-02">
            {{ $t(`hotel.reviews.title`) }}
          </ai-typo>
          <ai-typo
            class="AiReviews-description"
            as="p"
            variant="paragraph-04-regular">
            {{ $t('hotel.reviews.description') }}
          </ai-typo>
          <ai-link
            class="AiReviews-button"
            :text="$t(`hotel.reviews.buttonLabel`)"
            typo-variant="button-bold"
            :url="$t('hotel.reviews.link')"
            target="_blank"
            color="gold" />
        </grid-item>
        <grid-item sm="4" md="6">
          <div class="AiReviews-rating">
            <span class="AiReview-reviews-container">
              <ai-typo as="span" color="gold" variant="featured">
                {{ rating }}
              </ai-typo>
              <ai-typo
                class="AiReviews-rating--ligth"
                as="span"
                color="gold"
                variant="featured">
                /5
              </ai-typo>
            </span>
            <span class="AiReview-star-content">
              <span class="AiReview-star-content--star">
                <span class="sr-only">
                  {{
                    $t('accessibility.description.hotelRatingOn', {
                      ratingOn: rating,
                    })
                  }}
                </span>
                <ai-icon
                  v-for="index in activeRating"
                  :key="`AiRating-${index}`"
                  class="AiReview-star-content--item"
                  :name="type"
                  :size="size" />
                <ai-icon
                  v-if="halfRating"
                  class="AiReview-star-content--item AiReview-star-content--item-half"
                  :name="`${type}-half` as IconName"
                  :size="size" />
                <ai-icon
                  v-for="index in inactiveRating"
                  :key="`AiRating-${index}`"
                  class="AiReview-star-content--item AiReview-star-content--item-empty"
                  :name="`${type}-outline` as IconName"
                  :size="size" />
              </span>
              <ai-typo
                class="AiReview-reviews"
                as="p"
                variant="paragraph-04-bold"
                color="gold">
                {{ $t(`review.label`, { reviewsCount: reviewsCount }) }}
              </ai-typo>
            </span>
          </div>
          <ul id="hotelClientReviews" class="AiReviews-reviewContainer">
            <ai-review
              v-for="(clientReview, index) of reviews ?? []"
              :key="`AiClientReviews-item-${index}`"
              type="star"
              :review-number="index + 1"
              :author="clientReview.author ?? ''"
              :date="clientReview.created ?? ''"
              :description="clientReview.text ?? ''"
              :title="clientReview.title ?? ''"
              :score="clientReview.score ?? 0"
              :type-of-travel="clientReview.tripType ?? ''" />
          </ul>

          <span class="sr-only" aria-live="polite">
            {{ $t('review.label', { reviewsCount: reviews.length }) }}
          </span>

          <ai-button
            v-if="!allReviewsLoad"
            class="AiReview-button"
            :slim="true"
            icon-right="chevron-down"
            variant="tertiary"
            :label="$t('hotel.reviews.button.seeMore')"
            aria-controls="hotelClientReviews"
            @click="loadMore" />
        </grid-item>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { IconName } from '../../atoms/AiIcon/types';

import type { ReviewItem } from '~~/domains/graphql';

export interface AiClientReviewsProps {
  rating?: number | null;
  reviews?: ReviewItem[];
  size?: number;
  type: 'star';
  reviewsCount: number;
  hotelId: string;
  allReviewsLoad: boolean;
}

interface AiClientReviewsEmits {
  (event: 'loadMore'): void;
}

const props = withDefaults(defineProps<AiClientReviewsProps>(), {
  rating: 0,
  reviews: () => [],
  size: 18,
  type: 'star',
});

const emit = defineEmits<AiClientReviewsEmits>();

const halfRating = computed(() => ((props.rating ?? 0) * 10) % 10 === 5);
const activeRating = computed(() => Math.floor(props.rating ?? 0));
const inactiveRating = computed(
  () => 5 - activeRating.value - (halfRating.value ? 1 : 0),
);

const loadMore = () => emit('loadMore');
</script>
<style scoped lang="scss">
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/fonts';
@use '@/assets/styles/utilities/mq';

.AiReviews-header {
  margin-bottom: constants.$margin-04;
}
.AiReviews-title {
  margin-bottom: constants.$margin-02;
}
.AiReviews-description {
  margin-bottom: constants.$margin-02;
}
.AiReviewsBlock {
  padding-bottom: constants.$padding-07;
  padding-top: constants.$padding-07;
}
.AiReview-reviews-container {
  @include fonts.featured;
}
.AiReviews-rating {
  display: flex;
  flex-direction: column;
  padding: constants.$margin-01 constants.$margin-02;
  margin-bottom: constants.$margin-04;
  background: colors.$gold-100;
  @media (mq.$from-medium) {
    justify-content: space-between;
    align-items: center;
    flex-direction: row;
  }

  &--ligth {
    font-size: 0.7em;
  }
}
.AiReview-star-content {
  display: flex;
  &--star {
    display: flex;
    margin-right: constants.$inner-03;
  }
  &--item {
    fill: colors.$gold-900;
    &-half {
      fill: none;
    }
    &-empty {
      fill: none;
    }
  }
}

.AiReviews-button {
  text-transform: uppercase;
}
</style>
